<script setup lang="ts">
  // Compact branch/caja controls for the POS V2 header (instead of the
  // shell's V2ShellContextBar): same guards and selection flow
  // (useCajaSelection key modal), restyled as quiet chips + popovers.
  import { computed, ref } from 'vue';
  import { onClickOutside } from '@vueuse/core';
  import { IBranch } from '@/interfaces/IBranch';
  import { ICaja } from '@/interfaces/ICaja';
  import { useCajaSelection } from '@/composition/use-caja-selection';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const restaurantStore = useRestaurantStore();
  const { selectCaja: selectCajaWithModal } = useCajaSelection();

  const showBranchSelector = computed(
    () =>
      restaurantStore.effectiveTier > 1 &&
      (restaurantStore.branches?.length ?? 0) > 1
  );

  const showCajaSelector = computed(
    () =>
      restaurantStore.effectiveTier > 0 &&
      !!restaurantStore.selectedBranch?.settingUseCajas &&
      !!restaurantStore.cajas
  );

  const enabledBranches = computed(
    () => restaurantStore.branches?.filter((b) => b.active) ?? []
  );

  const enabledCajas = computed(
    () => restaurantStore.cajas?.filter((c: ICaja) => c.enabled) ?? []
  );

  const caja = computed(() => restaurantStore.caja);
  const isCajaClosed = computed(() => caja.value?.status === 'closed');

  //#region Popovers

  const branchOpen = ref(false);
  const cajaOpen = ref(false);

  const branchRef = ref<HTMLElement>();
  const cajaRef = ref<HTMLElement>();

  onClickOutside(branchRef, () => (branchOpen.value = false));
  onClickOutside(cajaRef, () => (cajaOpen.value = false));

  //#endregion

  function selectBranch(branch: IBranch) {
    branchOpen.value = false;
    if (branch.id === restaurantStore.selectedBranch?.id) return;
    restaurantStore.setSelectedBranch(branch);
  }

  function selectCaja(next: ICaja) {
    cajaOpen.value = false;
    if (next.id === caja.value?.id) return;
    selectCajaWithModal(next);
  }
</script>

<template>
  <div v-if="showBranchSelector || showCajaSelector" class="pos-v2-caja-chip">
    <!-- Branch chip -->
    <div v-if="showBranchSelector" ref="branchRef" class="chip-wrap">
      <button
        type="button"
        class="chip"
        :class="{ open: branchOpen }"
        @click="branchOpen = !branchOpen"
      >
        <PosUiIcon name="store-outline" class="chip-icon" />
        <span class="chip-label">
          {{ restaurantStore.selectedBranch?.name }}
        </span>
        <PosUiIcon name="chevron-down" class="chip-chevron" />
      </button>

      <div v-if="branchOpen" class="chip-menu">
        <div class="menu-heading">{{ $t('posV2.branchLabel') }}</div>
        <button
          v-for="branch in enabledBranches"
          :key="branch.id"
          type="button"
          class="menu-item"
          :class="{
            selected: branch.id === restaurantStore.selectedBranch?.id,
          }"
          @click="selectBranch(branch)"
        >
          <span class="item-label">{{ branch.name }}</span>
          <PosUiIcon
            v-if="branch.id === restaurantStore.selectedBranch?.id"
            name="check"
            class="item-check"
          />
        </button>
      </div>
    </div>

    <!-- Caja chip -->
    <div v-if="showCajaSelector" ref="cajaRef" class="chip-wrap">
      <button
        type="button"
        class="chip"
        :class="{ open: cajaOpen, 'chip--alert': !caja }"
        @click="cajaOpen = !cajaOpen"
      >
        <PosUiIcon name="cash-register" class="chip-icon" />
        <span class="chip-label">
          {{ caja ? caja.name : $t('posV2.cajaSelectPrompt') }}
        </span>
        <span
          v-if="caja"
          class="chip-dot"
          :class="isCajaClosed ? 'dot-closed' : 'dot-open'"
        ></span>
        <PosUiIcon name="chevron-down" class="chip-chevron" />
      </button>

      <div v-if="cajaOpen" class="chip-menu">
        <div class="menu-heading">{{ $t('posV2.cajaLabel') }}</div>
        <button
          v-for="c in enabledCajas"
          :key="c.id"
          type="button"
          class="menu-item"
          :class="{ selected: c.id === caja?.id }"
          @click="selectCaja(c)"
        >
          <span
            class="item-dot"
            :class="c.status === 'closed' ? 'dot-closed' : 'dot-open'"
          ></span>
          <span class="item-label">{{ c.name }}</span>
          <PosUiIcon v-if="c.id === caja?.id" name="check" class="item-check" />
        </button>

        <router-link
          v-if="caja"
          :to="`/${restaurantStore.restaurant.stringId}/caja`"
          class="menu-footer"
          @click.native="cajaOpen = false"
        >
          <span
            class="footer-dot"
            :class="isCajaClosed ? 'dot-closed' : 'dot-open'"
          ></span>
          {{ isCajaClosed ? $t('posV2.cajaClosed') : $t('posV2.cajaOpen') }}
          <PosUiIcon name="chevron-right" class="footer-icon" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-caja-chip {
    @apply tw-flex tw-flex-none tw-items-center tw-gap-1.5;

    .chip-wrap {
      @apply tw-relative;

      .chip {
        @apply tw-flex tw-h-10 tw-max-w-[13rem] tw-cursor-pointer tw-select-none tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-50;
        }

        &.open {
          @apply tw-border-neutral-400 tw-bg-neutral-50;
        }

        &.chip--alert {
          @apply tw-border-red-300 tw-bg-red-50 tw-text-red-700;

          &:hover {
            @apply tw-bg-red-100;
          }

          .chip-icon,
          .chip-chevron {
            @apply tw-text-red-500;
          }
        }

        .chip-icon {
          @apply tw-flex-none tw-text-lg tw-text-neutral-400;
        }

        .chip-label {
          @apply tw-min-w-0 tw-truncate;
        }

        .chip-dot {
          @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full;
        }

        .chip-chevron {
          @apply tw-flex-none tw-text-base tw-text-neutral-400;
        }
      }

      .chip-menu {
        @apply tw-absolute tw-right-0 tw-top-full tw-z-30 tw-mt-1 tw-w-56 tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-py-1 tw-shadow-lg;

        .menu-heading {
          @apply tw-px-3 tw-pb-1 tw-pt-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-400;
        }

        .menu-item {
          @apply tw-flex tw-min-h-[44px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors;

          &:hover {
            @apply tw-bg-neutral-50;
          }

          &.selected {
            @apply tw-bg-orange-50 tw-font-medium tw-text-orange-700;
          }

          .item-dot {
            @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full;
          }

          .item-label {
            @apply tw-min-w-0 tw-flex-1 tw-truncate;
          }

          .item-check {
            @apply tw-flex-none tw-text-base tw-text-orange-600;
          }
        }

        .menu-footer {
          @apply tw-mt-1 tw-flex tw-min-h-[40px] tw-items-center tw-gap-2 tw-border-t tw-border-neutral-100 tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-600 tw-transition-colors;

          &:hover {
            @apply tw-bg-neutral-50 tw-text-neutral-900;
          }

          .footer-dot {
            @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full;
          }

          .footer-icon {
            @apply tw-ml-auto tw-text-base tw-text-neutral-400;
          }
        }
      }
    }

    .dot-open {
      @apply tw-bg-emerald-500;
    }

    .dot-closed {
      @apply tw-bg-red-500;
    }
  }
</style>
